<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    UsersService,
    type CustomerUser as User,
    type PatchedCustomerUserRequest
  } from '@/types/generated'
  import { ROUTES } from '@/router/routes'

  const { t } = useTranslation({
    en: {
      profileSettings: 'Profile Settings',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      avatar: 'Profile Picture',
      changePassword: 'Change Password'
    },
    de: {
      profileSettings: 'Profiloptionen',
      firstName: 'Vorname',
      lastName: 'Nachname',
      email: 'E-Mail',
      phone: 'Telefon',
      avatar: 'Profilbild',
      changePassword: 'Passwort ändern'
    }
  })

  const router = useRouter()
  const user = ref<User>()

  onMounted(async () => {
    user.value = (await UsersService.usersRetrieve({ uuid: 'current' })) as User
  })

  const handleSave = async (values: Partial<User>) => {
    await UsersService.usersPartialUpdate({
      uuid: 'current',
      requestBody: values as PatchedCustomerUserRequest
    })
  }
</script>

<template>
  <SettingsPage>
    <AutoSavingForm
      v-if="user"
      @save="handleSave"
    >
      <div class="mb-3 block font-medium">{{ t('profileSettings') }}</div>

      <InputField
        v-model="user.first_name"
        name="first_name"
        :label="t('firstName')"
      />

      <InputField
        v-model="user.last_name"
        name="last_name"
        :label="t('lastName')"
      />

      <InputField
        v-model="user.email"
        name="email"
        type="email"
        :label="t('email')"
        disabled
      />

      <InputField
        v-model="user.phone"
        name="phone"
        type="tel"
        :label="t('phone')"
      />

      <div class="mt-6">
        <a
          :href="ROUTES.APP.SETTINGS.USER.PASSWORD"
          class="inline-block rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          @click.prevent="router.push(ROUTES.APP.SETTINGS.USER.PASSWORD)"
        >
          {{ t('changePassword') }}
        </a>
      </div>
    </AutoSavingForm>
  </SettingsPage>
</template>
